import { useOrgUsers } from "@/api/users";
import { useCallback, useMemo } from "react";

/**
 * The agent's reserved author id. It has no user row and never authenticates;
 * the v1 agent writes no comments, and the backend stamps only its
 * thread-metadata edits with it. Surfaces that render an author id resolve it
 * to a name and a mark. Must match the backend's constant.
 */
export const AGENT_USER_ID = "cohesive-agent";

/** What `AGENT_USER_ID` reads as everywhere it is rendered. */
export const AGENT_USER_NAME = "Cohesive";

/**
 * Display-name lookup over the org user list, for surfaces that render a user
 * id outside a Liveblocks component. Same react-query entry (`["users"]`) the
 * provider's `resolveUsers` fetches, so these reads share one cache rather
 * than opening a second fetch path.
 *
 * Undefined for an id the list does not carry — the caller decides what an
 * unresolved user reads as, and a raw uuid is never it.
 */
export function useUserDisplayName(): (userId: string) => string | undefined {
  const { data: users } = useOrgUsers();

  const namesById = useMemo(
    () => new Map((users ?? []).map((user) => [user.id, user.name])),
    [users],
  );

  return useCallback(
    (userId: string) =>
      userId === AGENT_USER_ID
        ? AGENT_USER_NAME
        : namesById.get(userId) || undefined,
    [namesById],
  );
}
